// Browser-native image compression for the import dialog. Fork-only.
//
// The import dialog compresses oversized images before adding them to the
// scene. This module does the decode and encode work with browser APIs only.

import {
  COMPRESSION_FORMATS,
  COMPRESSION_QUALITY_LADDER,
  COMPRESSION_SCALE_LADDER,
  MAX_DIMENSION,
} from "./config";

export type EncodeOptions = {
  type: string;
  quality: number;
  scale: number;
};

export type EncodeFn = (
  source: CanvasImageSource & { width: number; height: number },
  options: EncodeOptions,
) => Promise<Blob | null>;

export type DecodeFn = (
  file: File,
) => Promise<CanvasImageSource & { width: number; height: number }>;

export type CompressImageResult = {
  file: Blob;
  bytes: number;
  width: number;
  height: number;
  format: string;
  quality: number;
  scale: number;
  fits: boolean;
};

export type CompressImageOptions = {
  format: string;
  maxBytes: number;
  decodeFn?: DecodeFn;
};

/**
 * Decode an image file into a drawable source.
 *
 * Prefers createImageBitmap, falls back to an <img> plus an object URL. The
 * object URL is always revoked after the image loads.
 */
export async function decodeImage(
  file: File,
): Promise<CanvasImageSource & { width: number; height: number }> {
  // SVG must go through the <img> path: createImageBitmap cannot decode it in
  // every browser.
  const isSvg = file.type === "image/svg+xml";
  if (!isSvg && typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall back to the <img> path below.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to decode image"));
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Draw a source onto a canvas at the scaled size and encode it as a blob.
 *
 * Returns null when the browser cannot encode the requested format, so the
 * caller can move to the next format.
 */
export async function encode(
  source: CanvasImageSource & { width: number; height: number },
  options: EncodeOptions,
): Promise<Blob | null> {
  const { type, quality, scale } = options;
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  // JPEG has no alpha channel, so a transparent PNG would otherwise come out
  // with black edges. Paint white first. WebP keeps alpha, so it must not get
  // the white fill.
  if (type === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(source, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
  if (!blob) {
    return null;
  }
  // A browser that cannot encode the requested format silently returns a
  // different type (e.g. PNG for WebP). Trust the returned type, not the
  // request.
  if (blob.type !== type) {
    return null;
  }
  return blob;
}

/**
 * Compress an image file until it fits under maxBytes.
 *
 * Walks the quality ladder first, then drops to the next scale and walks
 * quality again. Returns the best attempt even when nothing fits, so the
 * caller can still show the smallest achievable result.
 */
export async function compressImageFile(
  file: File,
  options: CompressImageOptions,
  encodeFn: EncodeFn = encode,
): Promise<CompressImageResult | null> {
  const { format, maxBytes } = options;
  const decodeFn = options.decodeFn ?? decodeImage;

  const source = await decodeFn(file);
  const baseWidth = source.width;
  const baseHeight = source.height;
  const maxDim = Math.max(baseWidth, baseHeight);
  // Upstream normally resizes on import; our guard bypasses that path, so cap
  // the starting dimensions here.
  const capScale = maxDim > MAX_DIMENSION ? MAX_DIMENSION / maxDim : 1;

  // Try the preferred format first, then the rest. A browser that cannot
  // encode a format makes the encoder return null, so we move on.
  const formats = [format, ...COMPRESSION_FORMATS.filter((f) => f !== format)];

  let best: CompressImageResult | null = null;
  formatLoop: for (const currentFormat of formats) {
    for (const scale of COMPRESSION_SCALE_LADDER) {
      const effectiveScale = scale * capScale;
      for (const quality of COMPRESSION_QUALITY_LADDER) {
        const blob = await encodeFn(source, {
          type: currentFormat,
          quality,
          scale: effectiveScale,
        });
        if (!blob) {
          // Unsupported format: the encoder returned null. Move to the next
          // format instead of continuing the ladder.
          continue formatLoop;
        }
        const width = Math.max(1, Math.round(baseWidth * effectiveScale));
        const height = Math.max(1, Math.round(baseHeight * effectiveScale));
        const attempt: CompressImageResult = {
          file: blob,
          bytes: blob.size,
          width,
          height,
          format: currentFormat,
          quality,
          scale: effectiveScale,
          fits: blob.size <= maxBytes,
        };
        if (!best || attempt.bytes < best.bytes) {
          best = attempt;
        }
        if (attempt.fits) {
          return attempt;
        }
      }
    }
    // This format's ladder is exhausted without fitting. Return the best
    // attempt for it instead of trying the next format.
    if (best) {
      return best;
    }
  }
  // Every format was unsupported. Nothing could be encoded.
  return best;
}
